import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Link } from '@tanstack/react-router';
import { BuildingIcon, FolderUpIcon, House, SearchIcon } from 'lucide-react';

import { useEstateOptions } from '@/api/estate-options';
import { StatusType } from '@/features/estates/constants/status-type';
import { EstateImage } from '@/features/estates-property/components/estate-image';
import { type SearchAllResult, useSearchAll } from '@/features/search/api/search';
import { useClickOutside } from '@/hooks/use-click-outside';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';

import { Card } from '../ui/card';
import { Input } from '../ui/input';
import Spinner from '../ui/spinner';

export const GlobalSearchInput = () => {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [opened, setOpened] = useState(false);

  const openMenu = () => setOpened(true);

  const closeMenu = () => setOpened(false);

  const containerRef = useClickOutside<HTMLDivElement>(closeMenu, opened);

  const { data: estateOptions } = useEstateOptions();

  const { propertyTypeMap, marketingTypeMap } = useMemo(() => {
    return {
      propertyTypeMap: estateOptions?.propertyType
        ? estateOptions.propertyType.reduce<Record<string, string>>((acc, item) => {
            acc[item.id] = item.title;
            return acc;
          }, {})
        : {},
      marketingTypeMap: estateOptions?.marketingType
        ? estateOptions.marketingType.reduce<Record<string, string>>((acc, item) => {
            acc[item.id] = item.title;
            return acc;
          }, {})
        : {},
    };
  }, [estateOptions]);

  const { data: results, isLoading } = useSearchAll({
    query: {
      searchType: 'all',
      pageSize: 100,
      filters: { searchString },
    },
  });

  const debouncedInputChange = useDebouncedCallback((value: string) => {
    setSearchString(value);
  });

  const projectsLength = (results as SearchAllResult)?.projects?.length ?? 0;
  const estatesLength = (results as SearchAllResult)?.estates?.length ?? 0;
  const totalResults = projectsLength + estatesLength;
  const hasResults = !!results && totalResults > 0;

  const handleClickCard = () => {
    setInputValue('');
    setSearchString('');
    closeMenu();
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <Input
        size="lg"
        className="h-10 border-2"
        placeholder={t('search')}
        wrapperClassName="hidden sm:block"
        onFocus={openMenu}
        value={inputValue}
        onChange={(ev) => {
          setInputValue(ev.target.value);
          debouncedInputChange(ev.target.value);
        }}
        startIcon={<SearchIcon className="text-primary-light" />}
      />
      {!opened ? null : (
        <div
          className={cn(
            'absolute top-12 z-50 w-screen max-w-screen-sm animate-fade-in overflow-y-scroll rounded-lg bg-white shadow-lg transition-all duration-100',
            results ? (hasResults ? 'max-h-[580px] px-4 pb-4' : 'max-h-96') : 'max-h-96',
            results ? (totalResults > 4 ? 'h-screen' : 'h-fit') : 'h-96'
          )}
        >
          {isLoading ? (
            <div className="flex h-96 items-center justify-center">
              <Spinner className="size-12 [&_*]:stroke-primary-dark" />
            </div>
          ) : hasResults ? (
            <div className="py-3">
              {projectsLength > 0 ? (
                <div className="mt-2">
                  <div className="px-1 text-xs font-semibold uppercase text-gray-500">
                    {t('projects')}
                  </div>
                  {((results as SearchAllResult).projects || []).map((project) => (
                    <Link
                      key={`project-${project.id}`}
                      to="/projects/$projectId"
                      params={{ projectId: project.id }}
                      onClick={handleClickCard}
                    >
                      <Card
                        className="mt-2 flex h-[100px] items-center gap-x-2"
                        style={{ boxShadow: '0px 4px 15px 0px #00000040' }}
                      >
                        <div className="h-full min-h-[100px] w-[140px]">
                          <EstateImage
                            className="aspect-auto h-full max-h-[140px] min-h-[100px] w-[140px] rounded"
                            {...(project.thumbnail?.mediaId
                              ? {
                                  thumbnailId: project.thumbnail.mediaId,
                                }
                              : {})}
                            iconSize={28}
                          />
                        </div>
                        <div className="flex h-full flex-1 flex-col gap-y-2 py-[10px] pl-4">
                          <div className="flex gap-x-2">
                            {project.details?.externalId ? (
                              <div className="flex h-5 items-center whitespace-nowrap rounded-2xl bg-[#07B1BE1F] px-4 text-sm text-primary-dark">
                                {`#${project.details.externalId}`}
                              </div>
                            ) : null}
                            {project.status === StatusType.draft ||
                            project.status === StatusType.published ? (
                              <div className="flex h-5 items-center whitespace-nowrap rounded-2xl bg-[#07B1BE1F] px-4 text-sm text-primary-dark">
                                {project.status === StatusType.draft ? t('draft') : t('public')}
                              </div>
                            ) : null}
                          </div>

                          <div className="flex flex-1 items-start gap-x-2">
                            <div className="min-w-[250px] flex-1 text-sm font-bold leading-tight">
                              {project.name}
                            </div>
                          </div>
                          {project.address?.address ? (
                            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium leading-tight">
                              {project.address.address}
                            </div>
                          ) : null}
                        </div>
                        <div className="ml-auto pr-4 text-primary-light">
                          <BuildingIcon size={24} />
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : null}

              {((results as SearchAllResult).estates?.length ?? 0) > 0 ? (
                <div className="mt-4">
                  <div className="px-1 text-xs font-semibold uppercase text-gray-500">
                    {t('estates')}
                  </div>
                  {((results as SearchAllResult).estates || []).map((estate) => (
                    <Link
                      key={`estate-${estate.id}`}
                      to="/estates/$estateId"
                      params={{ estateId: estate.id }}
                      onClick={handleClickCard}
                    >
                      <Card
                        className="mt-2 flex gap-x-2"
                        style={{ boxShadow: '0px 4px 15px 0px #00000040' }}
                      >
                        <div className="min-h-[100px] w-[140px]">
                          <EstateImage
                            className="aspect-auto h-full max-h-[140px] min-h-[100px] w-[140px] rounded"
                            {...(estate.thumbnail?.mediaId
                              ? {
                                  thumbnailId: estate.thumbnail.mediaId,
                                }
                              : {})}
                            iconSize={28}
                          />
                        </div>
                        <div className="flex flex-1 flex-col gap-y-2 py-[10px] pl-4">
                          <div className="flex flex-wrap gap-2">
                            {estate.estateDetails?.externalId ? (
                              <div className="flex h-5 items-center whitespace-nowrap rounded-2xl bg-[#07B1BE1F] px-4 text-sm text-primary-dark">
                                {`#${estate.estateDetails.externalId}`}
                              </div>
                            ) : null}
                            {estate.estateDetails?.propertyType &&
                            propertyTypeMap[estate.estateDetails.propertyType] ? (
                              <div className="flex h-5 items-center whitespace-nowrap rounded-2xl bg-[#07B1BE1F] px-4 text-sm text-primary-dark">
                                {propertyTypeMap[estate.estateDetails.propertyType]}
                              </div>
                            ) : null}

                            {estate.estateDetails?.marketType &&
                            marketingTypeMap[estate.estateDetails.marketType] ? (
                              <div className="flex h-5 items-center whitespace-nowrap rounded-2xl bg-[#07B1BE1F] px-4 text-sm text-primary-dark">
                                {marketingTypeMap[estate.estateDetails.marketType]}
                              </div>
                            ) : null}

                            {estate.status === StatusType.draft ||
                            estate.status === StatusType.published ? (
                              <div className="flex h-5 items-center whitespace-nowrap rounded-2xl bg-[#07B1BE1F] px-4 text-sm text-primary-dark">
                                {estate.status === StatusType.draft ? t('draft') : t('public')}
                              </div>
                            ) : null}
                          </div>
                          <div className="flex flex-1 items-start gap-x-2">
                            <div className="min-w-[250px] flex-1 text-sm font-bold leading-tight">
                              {estate.name}
                            </div>
                          </div>
                          {estate.address?.address ? (
                            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium leading-tight">
                              {estate.address.address}
                            </div>
                          ) : null}
                        </div>
                        <div className="ml-auto flex items-center pr-4 text-primary-light">
                          <FolderUpIcon size={24} />
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <Trans
              t={t}
              i18nKey="no-estates-found"
              components={{
                label: <span className="font-medium text-primary-light" />,
                wrapper: <div className="flex h-96 items-center justify-center" />,
                icon: (
                  <House
                    viewBox="-1 0 25 25"
                    width="38"
                    height="38"
                    className="[&_*]:stroke-primary-light"
                  />
                ),
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
